<!--
  @component

  Svelte Query devtools component.

  Place the <Devtools /> component near the root of your page.
  It must have access to the Svelte Query context.

  ```svelte
  <script lang="ts">
  import { provideAPIClients } from '$lib/api';
  import Devtools from '$lib/components/devtools.svelte';

  provideAPIClients();
  </script>

  <Devtools />
  ```

  The devtools will always be enabled on `localhost`.
  For other environments, use `enableDevtools` and `disableDevtools`
  to change the setting, persisted via `localStorage`

  ```ts
  // turn devtools on
  enableDevtools()

  // turn devtools off
  disableDevtools()
  ```

  Based on [@tanstack/svelte-query-devtools][],
  customized to allow enabling in production environments.

  [@tanstack/svelte-query-devtools]: https://github.com/TanStack/query/blob/46f99f021e11648082d24819b13bf855334b2aff/packages/svelte-query-devtools/src/Devtools.svelte
 -->

<script lang="ts">
import { onMount } from 'svelte';
import type { TanstackQueryDevtools } from '@tanstack/query-devtools';
import { onlineManager, useQueryClient } from '$lib/api/tanstack-query';

import { dev as isDevServer } from '$app/environment';

const ENABLE_DEVTOOLS_KEY = 'viamEnableDevtools';
const client = useQueryClient();

let devtools: TanstackQueryDevtools | undefined;
let ref: HTMLDivElement | undefined;
let isMounted = false;

const configureDevtools = async (isEnabled = true) => {
  saveIsEnabled(isEnabled);
  unmountDevtools();

  if (isEnabled) {
    const { TanstackQueryDevtools } = await import('@tanstack/query-devtools');

    devtools = new TanstackQueryDevtools({
      client,
      onlineManager,
      queryFlavor: 'Svelte Query',
      version: '6',
      buttonPosition: 'bottom-left',
      position: 'bottom',
      initialIsOpen: false,
      errorTypes: [],
    });
  }
};

const saveIsEnabled = (isEnabled: boolean): void => {
  localStorage.setItem(ENABLE_DEVTOOLS_KEY, JSON.stringify(isEnabled));
};

const getIsEnabled = (): boolean => {
  const rawValue = localStorage.getItem(ENABLE_DEVTOOLS_KEY);

  if (isDevServer) {
    return true;
  }

  try {
    return rawValue ? JSON.parse(rawValue) === true : false;
  } catch {
    return false;
  }
};

const unmountDevtools = () => {
  if (isMounted) {
    devtools?.unmount();
    isMounted = false;
  }
};

onMount(() => {
  void configureDevtools(getIsEnabled());

  window.enableDevtools = async () => configureDevtools(true);
  window.disableDevtools = async () => configureDevtools(false);

  return unmountDevtools;
});

// remount when `devtools` or `ref` changes for initial mount and hot reload
$: if (devtools) {
  unmountDevtools();
  if (ref) {
    devtools.mount(ref);
    isMounted = true;
  }
}
</script>

<div
  class="tsqd-parent-container"
  bind:this={ref}
></div>
